import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Container, Form, Modal } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import { LuGraduationCap } from 'react-icons/lu' 
import { BsArrowClockwise, BsFunnelFill, BsSearch } from 'react-icons/bs'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import { Dropdown } from "react-bootstrap";
import {
  BsThreeDotsVertical,
  BsEye,
  BsPencilFill,
  BsTrash
} from "react-icons/bs";


import EditQueryModal from './EditQueryModal' 

const STATUS_CONFIG = {
  open:        { label: 'Open',        bg: '#EFF6FF', color: '#1D4ED8', dot: '#3B82F6' },
  in_progress: { label: 'In Progress', bg: '#FFFBEB', color: '#92400E', dot: '#F59E0B' },
  resolved:    { label: 'Resolved',    bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
  closed:      { label: 'Closed',      bg: '#F3F4F6', color: '#374151', dot: '#9CA3AF' }}

const PRIORITY_CONFIG = {
  low:      { label: 'Low',      bg: '#F0FDF4', color: '#166534' },
  medium:   { label: 'Medium',   bg: '#EFF6FF', color: '#1E40AF' },
  high:     { label: 'High',     bg: '#FFF7ED', color: '#9A3412' },
  critical: { label: 'Critical', bg: '#FEF2F2', color: '#991B1B' }}

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
      fontWeight: 700, letterSpacing: '0.3px', background: cfg.bg,
      color: cfg.color, fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap'}}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  )
}

const PriorityBadge = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority?.toLowerCase()] || PRIORITY_CONFIG.medium
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
      fontSize: '11px', fontWeight: 700, background: cfg.bg,
      color: cfg.color, fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap'}}>
      {cfg.label}
    </span>
  )
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'})
}

export default function MyQueries() {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [showFilterCard, setShowFilterCard] = useState(false)

  const [queries, setQueries] = useState([])
  const [allQueries, setAllQueries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [showViewModal, setShowViewModal] = useState(false);
const [showEditModal, setShowEditModal] = useState(false);
const [selectedQuery, setSelectedQuery] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`)
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const fetchQueries = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await ApiEndpoints.getMyQueries()
      const data = response.data?.data || []
      setQueries(data)
      setAllQueries(data)
    } catch (err) {
      console.error('Error fetching queries:', err.response?.data || err.message)
      setError('Failed to load queries. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchQueries() }, [fetchQueries])

  const filteredQueries = useMemo(() => {
    if (!search.trim()) return queries
    const q = search.trim().toLowerCase()
    return queries.filter((item) =>
      Object.values(item).some((val) => val != null && val.toString().toLowerCase().includes(q)),
    )
  }, [queries, search])

  const totalPages = Math.ceil(filteredQueries.length / itemsPerPage)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentQueries = filteredQueries.slice(indexOfFirstItem, indexOfLastItem)

  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(1)
    }
  }, [filteredQueries, totalPages])

  const handleRefresh = () => {
    setSearch('')
    setQueries(allQueries)
    setCurrentPage(1)
    fetchQueries()
  }

  const handleView = (query) => {
   setSelectedQuery(query);
  setShowViewModal(true);
};

const handleEdit = (query) => {
  setSelectedQuery(query);
  setShowEditModal(true);
};

const handleDelete = async (query) => {
   if (!window.confirm("Are you sure you want to delete this query?")) {
    return;
   }

   try {
    await ApiEndpoints.deleteQuery(query.id);

    setQueries((prev) =>
      prev.filter((item) => item.id !== query.id)
    );

  } catch (error) {
    console.error(error.response?.data || error.message);
  }

};



  return (
    <>
      <style>{`
        .dropdown-toggle-no-caret::after { display: none !important; }
        @media screen and (max-width: 40em) {
          .my-queries-table .responsiveTable td.pivoted {
            white-space: normal !important;
            overflow: visible !important;
            max-width: none !important;
            text-overflow: unset !important;
            word-break: break-word;
          }
          .my-queries-table .responsiveTable td.my-queries-title-cell {
            white-space: normal !important;
            overflow: visible !important;
            max-width: none !important;
            text-overflow: unset !important;
            word-break: break-word;
          }
        }
      `}</style>

      <div style={{
        marginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
        marginTop: '0px',
        marginRight: '0px',
        marginBottom: '0px',
        paddingLeft: isCollapsed ? '12px' : '12px',
        paddingRight: isCollapsed ? '12px' : '12px'}}>

        {/* ── Header ── */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%'}}
            >
              {/* Left: Logo + Title */}
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0}}>
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                >
                  My Queries
                </h3>
              </div>

              {/* Right: Profile */}
              {!isMobile && (
                <ProfileDropdown logout={logout} />
              )}
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >

          {/* ── Controls row: items per page + filter toggle ── */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              {/* Items Per Page */}
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4'}}
              >
                <span style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                  onFocus={(e) => { e.target.style.borderColor = '#223843' }}
                  onBlur={(e) => { e.target.style.borderColor = '#D1D3D4' }}
                >
                  <option value="10">10 per page</option>
                  <option value="20">20 per page</option>
                  <option value="30">30 per page</option>
                </Form.Select>
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
          </div>

          {/* ── Filter Card (collapsible) ── */}
          <div
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0'}}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'}}
              >
                {/* Filter Card Header */}
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6'}}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'}}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search & Filter Queries
                        </h5>
                        <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                          Find queries quickly with search
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        disabled={loading}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          opacity: loading ? 0.7 : 1,
                          transition: 'all 0.2s ease'}}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'scale(1.05)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Filter Card Body */}
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <div
                    style={{
                      padding: '20px',
                      backgroundColor: '#F3F4F6',
                      borderRadius: '8px',
                      border: '1px solid #D1D3D4'}}
                  >
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: '#10B981',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'}}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '16px' }} />
                      </div>
                      <label className="fw-semibold mb-0" style={{ color: '#111827', fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        Search Queries
                      </label>
                    </div>

                    <div style={{ position: 'relative' }}>
                      <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        Search by title, category, status...
                      </label>
                      <div style={{ position: 'relative' }}>
                        <BsSearch
                          style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#6b7280',
                            fontSize: '16px',
                            zIndex: 1}}
                        />
                        <Form.Control
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Enter title, category, status..."
                          style={{
                            paddingLeft: '40px',
                            borderRadius: '8px',
                            border: '1px solid #D1D3D4',
                            fontSize: '14px',
                            height: '40px',
                            transition: 'all 0.2s ease',
                            backgroundColor: '#ffffff',
                            fontFamily: 'Inter, system-ui, sans-serif'}}
                          onFocus={(e) => { e.target.style.borderColor = '#223843' }}
                          onBlur={(e) => { e.target.style.borderColor = '#D1D3D4' }}
                        />
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* ── Error alert ── */}
          {error && (
            <div
              className="alert mb-3"
              style={{
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                color: '#991B1B',
                borderRadius: '10px',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '13px'}}
            >
              {error}
            </div>
          )}

          {/* ── Table ── */}
          <Card className="my-queries-table" style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
            <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <Thead>
                <Tr style={{ backgroundColor: '#F3F4F6' }}>
                  {[ 'Title', 'Category', 'Priority', 'Status', 'Created At', "Action"].map((h) => (
                    <Th
                      key={h}
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        padding: '16px',
                        border: 'none',
                        borderBottom: '1px solid #D1D3D4',
                        textAlign: 'left',
                        color: '#111827',
                        fontFamily: 'Inter, system-ui, sans-serif'}}
                    >
                      {h}
                    </Th>
                  ))}
                </Tr>
              </Thead>

              <Tbody>
                {loading ? (
                  <Tr>
                    <Td
                      colSpan="6"
                      style={{
                        textAlign: 'center',
                        padding: '40px',
                        fontSize: '14px',
                        color: '#6b7280',
                        fontFamily: 'Inter, system-ui, sans-serif'}}
                    >
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: '#223843' }} />
                      Loading queries...
                    </Td>
                  </Tr>
                ) : currentQueries.length > 0 ? (
                  currentQueries.map((query, index) => (
                    <Tr
                      key={query.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#F3F4F6',
                        transition: 'background-color 0.2s ease',
                        borderBottom: '1px solid #D1D3D4'}}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6' }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#F3F4F6' }}
                    >
                      {/* <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, textAlign: 'left' }}>
                        #{query.id}
                      </Td> */}
                      <Td
                        className="my-queries-title-cell"
                        style={{
                          fontSize: '14px',
                          padding: '16px',
                          border: 'none',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontWeight: 600,
                          textAlign: 'left',
                          ...(isMobile
                            ? {
                                maxWidth: 'none',
                                overflow: 'visible',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }
                            : {
                                maxWidth: '260px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }),
                        }}
                        title={query.title}
                      >
                        {query.title}
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif', textAlign: 'left' }}>
                        {query.category || '-'}
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', textAlign: 'left' }}>
                        <PriorityBadge priority={query.priority} />
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', textAlign: 'left' }}>
                        <StatusBadge status={query.status} />
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif', textAlign: 'left' }}>
                        {formatDate(query.created_at)}
                      </Td>

<Td
  className="text-center"
  style={{ padding: '16px', border: 'none' }}
>
  {/* Desktop View - Dropdown */}
  <div className="d-none d-md-block">
    <Dropdown>
      <Dropdown.Toggle
        variant="light"
        className="dropdown-toggle-no-caret"
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          backgroundColor: 'transparent',
          border: '1px solid #D1D3D4',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease'}}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#223843'
          e.currentTarget.style.borderColor = '#223843'
          e.currentTarget.style.color = '#ffffff'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent'
          e.currentTarget.style.borderColor = '#D1D3D4'
          e.currentTarget.style.color = '#111827'
        }}
      >
        <BsThreeDotsVertical style={{ color: 'inherit' }} />
      </Dropdown.Toggle>

      <Dropdown.Menu
        style={{
          borderRadius: '8px',
          border: '1px solid #D1D3D4',
          padding: '8px',
          minWidth: '180px'}}
      >
        <Dropdown.Item
          onClick={() => handleEdit(query)}
          style={{
            fontSize: '13px',
            padding: '10px 12px',
            color: '#111827',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            fontFamily: 'Inter, system-ui, sans-serif'}}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#10B981'
            e.currentTarget.style.color = '#ffffff'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = '#111827'
          }}
        >
          <BsPencilFill size={16} /> View / Edit Query
        </Dropdown.Item>

        {/* <Dropdown.Item
          onClick={() => handleDelete(query)}
          style={{
            fontSize: '13px',
            padding: '10px 12px',
            color: '#dc3545',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            fontFamily: 'Inter, system-ui, sans-serif'}}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#FEE2E2'
            e.currentTarget.style.color = '#991B1B'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = '#dc3545'
          }}
        >
          <BsTrash size={16} /> Delete Query
        </Dropdown.Item> */}
      </Dropdown.Menu>
    </Dropdown>
  </div>

  {/* Mobile View - Circular Buttons */}
  <div className="d-md-none d-flex justify-content-start align-items-center gap-2">
    <button
      onClick={() => handleEdit(query)}
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        backgroundColor: '#10B981',
        border: 'none',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease'}}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#059669'
        e.currentTarget.style.transform = 'scale(1.1)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#10B981'
        e.currentTarget.style.transform = 'scale(1)'
      }}
      title="View / Edit Query"
    >
      <BsPencilFill size={16} />
    </button>

    {/* <button
      onClick={() => handleDelete(query)}
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        backgroundColor: '#EF4444',
        border: 'none',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease'}}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#DC2626'
        e.currentTarget.style.transform = 'scale(1.1)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#EF4444'
        e.currentTarget.style.transform = 'scale(1)'
      }}
      title="Delete Query"
    >
      <BsTrash size={16} />
    </button> */}
  </div>
</Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td
                      colSpan="7"
                      className="text-center"
                      style={{
                        padding: '40px',
                        fontSize: '14px',
                        color: '#6b7280',
                        fontFamily: 'Inter, system-ui, sans-serif'}}
                    >
                      {search ? 'No queries match your search.' : 'No queries found.'}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Card>

          <TablePagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            totalItems={filteredQueries.length}
            itemLabel="queries"
          />

        </div>
      </div>

      <Modal
  show={showViewModal}
  onHide={() => setShowViewModal(false)}
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>View Query</Modal.Title>
  </Modal.Header>

  <Modal.Body>
    {selectedQuery && (
      <>
        <p><strong>Title:</strong> {selectedQuery.title}</p>
        <p><strong>Category:</strong> {selectedQuery.category}</p>
        <p><strong>Priority:</strong> {selectedQuery.priority}</p>
        <p><strong>Status:</strong> {selectedQuery.status}</p>
        <p><strong>Description:</strong> {selectedQuery.description}</p>
      </>
    )}
  </Modal.Body>
</Modal>

    <EditQueryModal
  show={showEditModal}
  handleClose={() => setShowEditModal(false)}
  selectedQuery={selectedQuery}
  onUpdate={fetchQueries}
/> 

    </>
  )
}


// import React, { useEffect, useState } from "react";
// import ApiEndpoints from "./commonComponents/ApiEndpoints";

// const MyQueries = () => {
//   const [queries, setQueries] = useState([]);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     fetchQueries();
//   }, []);

//   const fetchQueries = async () => {
//     try {
//       const response = await ApiEndpoints.getMyQueries();

//       console.log("Queries:", response.data);

//       setQueries(response.data.data || []);
//     } catch (error) {
//       console.error(
//         "Error fetching queries:",
//         error.response?.data || error.message
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (loading) {
//     return <h4>Loading...</h4>;
//   }

//   return (
//     <div className="container mt-4">
//       <h2>My Queries</h2>

//       <table className="table table-bordered mt-3">
//         <thead>
//           <tr>
//             <th>ID</th>
//             <th>Title</th>
//             <th>Category</th>
//             <th>Priority</th>
//             <th>Status</th>
//             <th>Created</th>
//           </tr>
//         </thead>

//         <tbody>
//           {queries.length > 0 ? (
//             queries.map((query) => (
//               <tr key={query.id}>
//                 <td>{query.id}</td>
//                 <td>{query.title}</td>
//                 <td>{query.category}</td>
//                 <td>{query.priority}</td>
//                 <td>
//                   <span
//                     className={`badge ${
//                       query.status === "open"
//                         ? "bg-warning"
//                         : query.status === "resolved"
//                         ? "bg-success"
//                         : "bg-primary"
//                     }`}
//                   >
//                     {query.status}
//                   </span>
//                 </td>
//                 <td>
//                   {new Date(query.created_at).toLocaleDateString()}
//                 </td>
//               </tr>
//             ))
//           ) : (
//             <tr>
//               <td colSpan="6" className="text-center">
//                 No Queries Found
//               </td>
//             </tr>
//           )}
//         </tbody>
//       </table>
//     </div>
//   );
// };

// export default MyQueries;